import { Component, Signal, computed, input, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';

/** Bulk actions the bar can request. All four are executed by StaffTabComponent (MP-5692/MP-5693/MP-5694). */
export type BulkAction = 'message' | 'approve' | 'deny' | 'fire';

/** A selected position plus the status of the role it belongs to. */
export interface BulkSelectedPosition {
  _id: string;
  status: string;
  presumptive?: { status?: string } | null;
  isPresumptivelyPending?: boolean;
  reviewer: { _id: string; firstName: string; lastName: string };
  roleId: string;
  roleStatus: string;
}

export interface ActionState {
  disabled: boolean;
  tooltip: string;
}

/**
 * MP-5691 — fixed bottom action bar for the Staff tab (Option B).
 * Every action is always shown; invalid ones are disabled with a tooltip that names
 * the blocking candidates. Validity mirrors the per-row buttons in staff-list-table.
 *
 * Signal-based: inputs are signals and every derived value is a `computed`, so the
 * bar re-evaluates only when the selection or the tenant flag actually changes.
 */
@Component({
  selector: 'app-staff-bulk-action-bar',
  standalone: true,
  imports: [CommonModule, ButtonModule, TooltipModule],
  templateUrl: './bulk-action-bar.component.html',
  styleUrl: './bulk-action-bar.component.scss'
})
export class BulkActionBarComponent {
  static readonly MAX_NAMES_IN_TOOLTIP = 3;

  // ── Inputs / outputs ────────────────────────────────────────────────────────
  readonly selectedPositions = input<BulkSelectedPosition[]>([]);
  readonly isPWCEnabled = input<boolean>(false);

  readonly action = output<BulkAction>();
  readonly clearSelection = output<void>();

  // ── Derived state ───────────────────────────────────────────────────────────
  readonly count = computed(() => this.selectedPositions().length);
  readonly visible = computed(() => this.count() > 0);
  readonly approveLabel = computed(() => (this.isPWCEnabled() ? 'Confirm' : 'Approve'));

  readonly breakdown = computed(() => {
    const positions = this.selectedPositions();
    const staffed = positions.filter(p => this.isStaffed(p)).length;
    const pending = positions.filter(p => this.isPending(p)).length;
    const other = positions.length - staffed - pending;
    const parts: string[] = [];
    if (staffed) parts.push(`${staffed} Staffed`);
    if (pending) parts.push(`${pending} Pending`);
    if (other) parts.push(`${other} Other`);
    return parts.join(' · ');
  });

  readonly message: Signal<ActionState> = computed(() =>
    this.evaluate(p => this.canMessage(p), 'message', `Message ${this.plural('candidate')}`));

  readonly approve: Signal<ActionState> = computed(() =>
    this.evaluate(p => this.canApproveOrDeny(p), this.approveLabel().toLowerCase(), `${this.approveLabel()} ${this.plural('pending position')}`));

  readonly deny: Signal<ActionState> = computed(() =>
    this.evaluate(p => this.canApproveOrDeny(p), 'deny', `Deny ${this.plural('pending position')}`));

  readonly fire: Signal<ActionState> = computed(() =>
    this.evaluate(p => this.canFire(p), 'fire', `Fire ${this.plural('staffed candidate')}`));

  // ── Events ──────────────────────────────────────────────────────────────────
  onAction(action: BulkAction): void {
    this.action.emit(action);
  }

  onClear(): void {
    this.clearSelection.emit();
  }

  // ── Status classification (mirrors StaffTabComponent section rules) ─────────

  /** Staffed section: OCCUPIED with no presumptive ASSIGNED/DENIED and not presumptively pending. */
  isStaffed(p: BulkSelectedPosition): boolean {
    const presumptiveStatus = p.presumptive?.status;
    return p.status === 'OCCUPIED'
      && !p.isPresumptivelyPending
      && presumptiveStatus !== 'ASSIGNED'
      && presumptiveStatus !== 'DENIED';
  }

  /** Pending Approval section, excluding PRESUMPTIVE_QUIT rows which only offer "Send Offer". */
  isPending(p: BulkSelectedPosition): boolean {
    if (p.status === 'PRESUMPTIVE_QUIT') {
      return false;
    }
    if (p.isPresumptivelyPending) {
      return true;
    }
    return p.status === 'OCCUPIED' && p.presumptive?.status === 'ASSIGNED';
  }

  isArchived(p: BulkSelectedPosition): boolean {
    return p.roleStatus === 'ARCHIVE';
  }

  statusLabel(p: BulkSelectedPosition): string {
    if (this.isArchived(p)) return 'Archived role';
    if (this.isStaffed(p)) return 'Staffed';
    if (this.isPending(p)) return 'Pending';
    if (p.status === 'PRESUMPTIVE_QUIT') return 'Presumptive quit';
    return p.presumptive?.status === 'DENIED' ? 'Denied' : this.titleCase(p.status);
  }

  // ── Validity predicates (mirror the row buttons) ────────────────────────────
  canMessage(p: BulkSelectedPosition): boolean {
    return !this.isArchived(p) && (this.isStaffed(p) || this.isPending(p));
  }

  canFire(p: BulkSelectedPosition): boolean {
    return !this.isArchived(p) && this.isStaffed(p);
  }

  canApproveOrDeny(p: BulkSelectedPosition): boolean {
    return !this.isArchived(p) && this.isPending(p);
  }

  // ── Internals ───────────────────────────────────────────────────────────────
  private evaluate(predicate: (p: BulkSelectedPosition) => boolean, verb: string, enabledTooltip: string): ActionState {
    const positions = this.selectedPositions();
    if (!positions.length) {
      return { disabled: true, tooltip: '' };
    }
    if (positions.some(p => this.isArchived(p))) {
      return { disabled: true, tooltip: 'Role is archived — no actions available' };
    }
    const blockers = positions.filter(p => !predicate(p));
    if (!blockers.length) {
      return { disabled: false, tooltip: enabledTooltip };
    }
    return { disabled: true, tooltip: `Can’t ${verb}: ${this.describe(blockers)}` };
  }

  private describe(positions: BulkSelectedPosition[]): string {
    const max = BulkActionBarComponent.MAX_NAMES_IN_TOOLTIP;
    const names = positions.slice(0, max)
      .map(p => `${p.reviewer.firstName} ${p.reviewer.lastName} (${this.statusLabel(p)})`);
    const more = positions.length - max;
    return names.join(', ') + (more > 0 ? `, +${more} more` : '');
  }

  private plural(noun: string): string {
    const n = this.count();
    return `${n} ${noun}${n === 1 ? '' : 's'}`;
  }

  private titleCase(value: string): string {
    return value ? value.charAt(0) + value.slice(1).toLowerCase() : '';
  }
}
